import { lazy } from 'react';

import { LayoutType, PageType } from '@shared/constants/constants';
import {
  ACTION_BACK_PATH,
  PAYMENTS_ADD_BILL_PATH,
  PAYMENTS_CHECK_STATUS_PATH,
  PAYMENTS_DD_RECEIPT_PATH,
  PAYMENTS_OUTSTANDING_CLUSTER_PATH,
  PAYMENTS_OUTSTANDING_PATH,
  PAYMENTS_PATH,
  PAYMENTS_PUBLIC_ADD_BILL_PATH,
  PAYMENTS_PUBLIC_CHECK_STATUS_PATH,
  PAYMENTS_PUBLIC_PATH,
  PAYMENTS_PUBLIC_RECEIPT_PATH,
  PAYMENTS_RECEIPT_PATH,
  PAYMENTS_SUMMARY_PATH,
  PAYMENTS_SUMMARY_PUBLIC_PATH,
} from '@shared/constants/paths';
import { SERVICE_CATALOG_CODE } from '@shared/constants/serviceCatalog';
import type { RouteConfig } from '@shared/types/common.types';

const SelectPaymentClusterPage = lazy(() => import('./pages/SelectClusterPage/SelectPaymentClusterPage'));
const OutstandingBillPage = lazy(() => import('./pages/OutstandingBillPage/OutstandingBillPage'));
const AddBillPage = lazy(() => import('./pages/AddBillPage/AddBillPage'));
const PaymentSummaryPage = lazy(() => import('./pages/PaymentSummaryPage/PaymentSummaryPage'));
const CheckDirectDebitStatusPage = lazy(() => import('./pages/CheckDirectDebitStatusPage/CheckDirectDebitStatusPage'));
const ReceiptPage = lazy(() => import('./pages/ReceiptPage/ReceiptPage'));
const DDReceiptPage = lazy(() => import('./pages/DDReceiptPage/DDReceiptPage'));

export const PAYMENTS_ROUTES: RouteConfig[] = [
  {
    actionUrl: ACTION_BACK_PATH,
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SelectPaymentClusterPage />,
    displayTitle: () => 'Bills & Payments',
    pageType: PageType.PRIVATE,
    path: PAYMENTS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.payment,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: PAYMENTS_PATH,
    component: () => <OutstandingBillPage isSingpass />,
    displayTitle: () => 'Bills & Payments',
    pageType: PageType.PRIVATE,
    path: PAYMENTS_OUTSTANDING_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.payment,
  },
  {
    actionUrl: ACTION_BACK_PATH,
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <OutstandingBillPage isSingpass />,
    displayTitle: () => 'Bills & Payments',
    pageType: PageType.PRIVATE,
    path: PAYMENTS_OUTSTANDING_CLUSTER_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.payment,
  },
  {
    actionUrl: ACTION_BACK_PATH,
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <OutstandingBillPage />,
    displayTitle: () => 'Bills & Payments',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: PAYMENTS_PUBLIC_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicPayment,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <AddBillPage isSingpass />,
    displayTitle: () => 'Add Bill(s)',
    pageType: PageType.PRIVATE,
    path: PAYMENTS_ADD_BILL_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.payment,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <AddBillPage />,
    displayTitle: () => 'Add Bill(s)',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: PAYMENTS_PUBLIC_ADD_BILL_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicPayment,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <PaymentSummaryPage isSingpass />,
    displayTitle: () => 'Bills & Payments',
    pageType: PageType.PRIVATE,
    path: PAYMENTS_SUMMARY_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.payment,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <PaymentSummaryPage />,
    displayTitle: () => 'Bills & Payments',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: PAYMENTS_SUMMARY_PUBLIC_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicPayment,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <CheckDirectDebitStatusPage isSingpass />,
    displayTitle: () => 'Bills & Payments',
    isHideBackNavButton: true,
    pageType: PageType.PRIVATE,
    path: PAYMENTS_CHECK_STATUS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.payment,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <CheckDirectDebitStatusPage />,
    displayTitle: () => 'Bills & Payments',
    isHideBackNavButton: true,
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: PAYMENTS_PUBLIC_CHECK_STATUS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicPayment,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: PAYMENTS_PATH,
    component: () => <ReceiptPage isSingpass />,
    displayTitle: () => 'Bills & Payments',
    pageType: PageType.PRIVATE,
    path: PAYMENTS_RECEIPT_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.payment,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: PAYMENTS_PUBLIC_PATH,
    component: () => <ReceiptPage />,
    displayTitle: () => 'Bills & Payments',
    layoutProps: {
      contentClassName: 'lg:max-w-5xl',
    },
    pageType: PageType.PUBLIC,
    path: PAYMENTS_PUBLIC_RECEIPT_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicPayment,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <CheckDirectDebitStatusPage />,
    displayTitle: () => 'Bills & Payments',
    isHideBackNavButton: true,
    pageType: PageType.PRIVATE,
    path: PAYMENTS_CHECK_STATUS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.payment,
  },
  {
    availableViews: [LayoutType.COMMON],
    component: () => <DDReceiptPage />,
    displayTitle: () => 'Bills & Payments',
    pageType: PageType.PUBLIC,
    path: PAYMENTS_DD_RECEIPT_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.publicPayment,
  },
];
